<template>
    <gwb-form-v2
        ref="form"
        :data="formData"
        :submit="signupUser"
        :validation-rules="validationRules"
    >
        <template #default="{ validator, touch, apiErrors, loading }">
            <div class="signup-progress">
                <div
                    class="signup-progress__step"
                    v-for="n in activeStep"
                ></div>
            </div>

            <div
                class="signup-form__step"
                :class="[formData.step2.regRole]"
                v-show="activeStep === 1"
            >
                <p
                    v-if="initRegRole === 'worker'"
                    class="tc-purple text-center lead bold"
                >
                    {{ $lang("landing.signup_headline_welcome_worker") }}
                </p>
                <p
                    v-else-if="initRegRole === 'company'"
                    class="tc-purple text-center lead bold"
                >
                    {{ $lang("landing.signup_headline_welcome_company") }}
                </p>
                <p v-else class="tc-purple text-center lead bold">
                    {{ $lang("landing.signup_headline_welcome") }}
                </p>

                <gwb-float-field
                    v-model="formData.step1.email"
                    type="email"
                    id="email"
                    name="email"
                    :label="$lang('landing.signup_email')"
                    :error="validator.step1.email.$error"
                    @change="touch(validator.step1.email)"
                    @blur="
                        ($event) => {
                            validator.step1.email.$touch;
                            checkEmailSuggestion($event.target.value);
                        }
                    "
                ></gwb-float-field>
                <span
                    v-if="emailSuggestion && !validator.step1.email.$error"
                    class="is-visible form-error tc-purple"
                >
                    {{ $lang("landing.signup_email_suggestion") }}
                    <a
                        @click="
                            formData.step1.email = emailSuggestion.full;
                            emailSuggestion = null;
                        "
                    >
                        {{ emailSuggestion.full }}
                    </a>
                </span>
                <span
                    v-if="validator.step1.email.$error"
                    v-text="validator.step1.email.$errors[0].$message"
                    class="is-visible form-error"
                ></span>

                <gwb-float-field
                    v-model="formData.step1.password"
                    :type="passwordVisible ? 'text' : 'password'"
                    id="password"
                    name="password"
                    :label="$lang('landing.signup_login_pass')"
                    :error="validator.step1.password.$error"
                    @change="touch(validator.step1.password)"
                    @blur="touch(validator.step1.password)"
                >
                    <template #button>
                        <button
                            id="login-form-show-pswd-btn"
                            type="button"
                            @click="passwordVisible = !passwordVisible"
                            tabindex="-1"
                        >
                            <span v-if="!passwordVisible"
                                ><svg
                                    class="icon double"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 40 40"
                                >
                                    <path
                                        class="st0"
                                        d="M20.4 30.6c-5.3-.1-9.5-2-12.9-5.4-.6-.6-1.1-1.2-.4-2 .8-.8 1.4-.2 2.1.4 7.4 7 17.8 6.1 23.8-2.2.6-.8.7-1.4 0-2.2-6.9-9.6-19.9-9.2-26.3.6-.5.7-.8 2-2.1 1.3-1.3-.8-.3-1.7.2-2.6 7.5-11.7 23.4-11.5 30.7.4.5.7.7 1.4.2 2.2-3.6 5.9-8.7 9.2-15.3 9.5z"
                                    />
                                    <path
                                        class="st0"
                                        d="M25.7 20.2c0 3.2-2.6 5.8-5.7 5.8-3.2 0-5.8-2.6-5.8-5.7 0-3.2 2.5-5.8 5.7-5.8s5.8 2.5 5.8 5.7zm-2.2.1c0-1.9-1.5-3.5-3.4-3.5-1.8 0-3.4 1.5-3.4 3.3-.1 1.9 1.5 3.5 3.4 3.6 1.7 0 3.4-1.5 3.4-3.4z"
                                    /></svg
                            ></span>
                            <span v-else-if="passwordVisible"
                                ><svg
                                    class="icon double"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 40 40"
                                >
                                    <path
                                        class="st0"
                                        d="M20.4 29.7c-5.1-.1-9-1.9-12.4-5.2-.5-.5-1-1.2-.4-1.9.7-.8 1.4-.2 2 .4 7.1 6.7 17.1 5.8 22.7-2.1.5-.8.6-1.3 0-2.1-6.5-9.1-19-8.8-25.1.7-.5.7-.8 1.9-2 1.3-1.2-.8-.2-1.7.3-2.5 7.2-11.3 22.4-11.1 29.4.3.4.7.7 1.3.2 2.1-3.5 5.6-8.4 8.8-14.7 9z"
                                    />
                                    <path
                                        class="st0"
                                        d="M25.5 19.8c0 3-2.4 5.5-5.4 5.5-3 0-5.5-2.4-5.5-5.4 0-3 2.4-5.5 5.4-5.5 3-.1 5.5 2.4 5.5 5.4zm-2.2.1c0-1.8-1.5-3.3-3.3-3.3-1.8 0-3.2 1.4-3.3 3.2-.1 1.8 1.4 3.3 3.2 3.4 1.9 0 3.4-1.5 3.4-3.3z"
                                    />
                                    <path
                                        class="st0"
                                        d="M30.5 9.5c-.7-.7-1.8-.7-2.5 0l-19 19c-.7.7-.7 1.8 0 2.5s1.8.7 2.5 0l19-19c.7-.7.7-1.8 0-2.5z"
                                    /></svg
                            ></span>
                        </button>
                    </template>
                </gwb-float-field>
                <span
                    v-if="validator.step1.password.$error"
                    v-text="validator.step1.password.$errors[0].$message"
                    class="is-visible form-error"
                ></span>

                <div class="row align-center align-middle mt-2 signup-actions">
                    <div class="columns medium-12 signup-actions__primary">
                        <button
                            type="button"
                            id="signup-form-signup-btn"
                            :class="{ invalid: validator.step1.$error }"
                            class="
                                large
                                expanded
                                login
                                button
                                text-uppercase
                                bold
                                signup-actions__primary-btn
                            "
                            @click="gotoStep2(validator)"
                        >
                            {{ $lang("landing.signup_button") }}
                        </button>
                    </div>
                    <div class="columns medium-12 flex-initial sm:flex-auto">
                        <a
                            id="signup-form-signup-facebook-btn"
                            href="/auth/oauth-login/Facebook"
                            class="large expanded facebook button"
                            data-cookieconsent="ignore"
                            @click="trackSignupStarted('facebook', { useBeacon: true })"
                        >
                            <strong
                                class="hide-for-small-only text-uppercase"
                                >{{ $lang("landing.signup_button_fb") }}</strong
                            >
                            <svg
                                class="show-for-small-only icon double"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 40 40"
                            >
                                <path
                                    class="st0"
                                    d="M24.4 6.3c0 1.5-.1 3-.1 4.5h-3.1c-.3 0-.6 0-.9.1-.5.1-.8.4-1 .9 0 .1 0 .2-.1.2 0 .2-.1.5-.1.7v3.5h5c-.1 1.6-.2 3.2-.2 4.8H19v13.2h-5.1V20.7h-3.5v-4.8h3.5V12c0-.8.1-1.6.4-2.4.6-1.5 1.6-2.6 3.1-3.2.6-.3 1.2-.4 1.9-.5.7-.1 1.5-.1 2.2-.1.5 0 1.1 0 1.6.1.5.3.8.3 1.3.4-.1 0-.1 0 0 0z"
                                />
                            </svg>
                        </a>
                    </div>
                    <div class="columns medium-12 flex-initial sm:flex-auto">
                        <a
                            id="signup-form-signup-google-btn"
                            href="/auth/oauth-login/Google"
                            class="large expanded google button"
                            data-cookieconsent="ignore"
                            @click="trackSignupStarted('google', { useBeacon: true })"
                        >
                            <strong
                                class="hide-for-small-only text-uppercase"
                                >{{
                                    $lang("landing.signup_button_google")
                                }}</strong
                            >
                            <svg
                                class="show-for-small-only icon double"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 40 40"
                            >
                                <path
                                    class="st0"
                                    d="M19.8 9c.8 1 1.1 2.1 1.3 3.3.2 2.2-.6 3.9-2.2 5.3-.2.2-.5.4-.8.6l-.6.6c-.4.5-.4.9 0 1.4.5.5 1.1 1 1.6 1.5.4.4.8.7 1.2 1 1.2 1.1 1.8 2.5 1.9 4.1.1 2.9-1.3 5.2-4.2 6.4-1.3.6-2.7.8-4.1.9-2.1.1-4.2-.2-6.1-1.1-.6-.3-1.2-.7-1.7-1.1-1.7-1.4-2.1-3.3-1.2-5.2.9-1.9 2.4-3 4.2-3.8 1.2-.5 2.4-.7 3.7-.8h.4c-.4-.8-.5-1.6-.3-2.4-.6-.1-1.1-.2-1.7-.3-3.2-.8-5.2-3.5-5-6.8.1-1.5.8-2.7 1.8-3.7 1.6-1.6 3.6-2.5 5.9-2.5H22c.4 0 .6.1.8.5.1.3 0 .6-.4.8-.5.3-1 .7-1.4 1-.3.2-.6.4-1 .3h-.2zM14 31.5h.8c1-.1 1.9-.3 2.7-.8 1.5-.9 2.1-3.6.3-4.8-.6-.4-1.2-.9-1.9-1.3-.1 0-.1-.1-.2-.1-2.1-.5-4-.2-5.7 1.1-.5.4-1 1-1.1 1.7-.3 1.3.2 2.4 1.2 3.2 1.1.8 2.5 1.1 3.9 1zm-3.5-19.3c0 .3 0 .7.1 1 .3 1.6 1 2.9 2.4 3.9.7.5 1.5.6 2.3.3.8-.3 1.2-.9 1.4-1.7.5-1.3.3-2.6-.2-3.8-.5-1.2-1.2-2.2-2.4-2.8-1.3-.6-2.6-.2-3.2 1.2-.3.5-.4 1.2-.4 1.9z"
                                />
                                <path
                                    class="st0"
                                    d="M30.2 17.6h4.4c.6 0 .8.2.8.8v1c0 .5-.2.8-.8.8h-4.5v4.5c0 .6-.2.8-.9.8h-.8c-.9 0-1-.2-1-1v-4.3h-4.5c-.6 0-.8-.2-.8-.8v-1c0-.5.2-.8.8-.8h4.5v-4.5c0-.6.2-.8.8-.8h1c.5 0 .8.2.8.8v4.1c.2.2.2.3.2.4z"
                                />
                            </svg>
                        </a>
                    </div>
                </div>

                <div class="mt-1 text-center semibold">
                    <span class="block">{{
                        $lang("landing.signup_already_have_account")
                    }}</span>
                    <a
                        id="signup-form-login-link"
                        class="text-center"
                        href="/signin"
                        >{{ $lang("landing.signup_go_to_login") }}</a
                    >
                </div>
            </div>

            <div
                class="signup-form__step"
                :class="[formData.step2.regRole]"
                v-show="activeStep === 2"
            >
                <p class="tc-purple text-center lead bold">
                    {{ $lang("landing.signup_form2_headline") }}
                </p>

                <div class="mb-1">
                    <fieldset class="mb-1-half">
                        <div
                            class="p-1-half"
                            :class="{
                                'bg-green-20':
                                    formData.step2.regRole === 'worker',
                            }"
                        >
                            <input
                                type="radio"
                                name="regRole"
                                value="worker"
                                v-model="formData.step2.regRole"
                                @change="touch(validator.step2.regRole)"
                                id="roleWorker"
                                class="mb-0"
                            />
                            <label for="roleWorker" class="semibold">{{
                                $lang("landing.signup_form2_option1")
                            }}</label>
                        </div>
                        <div
                            class="p-1-half"
                            v-bind:class="{
                                'bg-green-20':
                                    formData.step2.regRole === 'company',
                            }"
                        >
                            <input
                                type="radio"
                                name="regRole"
                                value="company"
                                v-model="formData.step2.regRole"
                                @change="touch(validator.step2.regRole)"
                                id="roleCompany"
                                class="mb-0"
                            />
                            <label for="roleCompany" class="semibold">{{
                                $lang("landing.signup_form2_option2")
                            }}</label>
                        </div>
                    </fieldset>

                    <div
                        class="warning callout"
                        v-if="validator.step2.regRole.$error"
                    >
                        <p v-text="validator.step2.regRole.$errors[0].$message"></p>
                    </div>
                </div>

                <div class="row align-center">
                    <div class="columns medium-12">
                        <button
                            type="button"
                            id="signup-form-continue-to-step3-btn"
                            :class="{ invalid: validator.step2.$error }"
                            class="
                                large
                                expanded
                                login
                                button
                                text-uppercase
                                bold
                            "
                            @click="gotoStep3(validator)"
                        >
                            {{ $lang("landing.signup_form2_button") }}
                        </button>
                    </div>
                </div>

                <div class="mt-1 text-center semibold" v-if="!oAuthUser">
                    <a class="text-center" href="#" @click="gotoPreviousStep"
                        >&#10094; {{ $lang("m.back") }}</a
                    >
                </div>
            </div>

            <div
                class="signup-form__step"
                v-bind:class="[formData.step2.regRole]"
                v-show="activeStep === 3"
            >
                <p class="tc-purple text-center lead bold">
                    {{ $lang("landing.signup_form3_headline") }}
                </p>

                <div class="warning callout" v-if="apiErrors.length > 0">
                    <p v-for="error in apiErrors" v-text="error.title"></p>
                </div>
                <div class="warning callout" v-if="apiError">
                    <p v-text="apiError"></p>
                </div>

                <gwb-float-field
                    v-model="formData.step3.first_name"
                    type="text"
                    id="first_name"
                    name="first_name"
                    :label="$lang('landing.signup_form3_first')"
                    :error="validator.step3.first_name.$error"
                    @change="touch(validator.step3.first_name)"
                    @blur="touch(validator.step3.first_name)"
                ></gwb-float-field>
                <span
                    v-if="validator.step3.first_name.$error"
                    v-text="validator.step3.first_name.$errors[0].$message"
                    class="is-visible form-error"
                ></span>

                <gwb-float-field
                    v-model="formData.step3.last_name"
                    type="text"
                    id="last_name"
                    name="last_name"
                    :label="$lang('landing.signup_form3_last')"
                    :error="validator.step3.last_name.$error"
                    @change="touch(validator.step3.last_name)"
                    @blur="touch(validator.step3.last_name)"
                ></gwb-float-field>
                <span
                    v-if="validator.step3.last_name.$error"
                    v-text="validator.step3.last_name.$errors[0].$message"
                    class="is-visible form-error"
                ></span>

                <template v-if="oAuthUser && !oAuthUser.email">
                    <gwb-float-field
                        v-model="formData.step3.email"
                        type="email"
                        id="email"
                        name="email"
                        :label="$lang('landing.signup_email')"
                        :error="validator.step3.email.$error"
                        @change="touch(validator.step3.email)"
                        @blur="touch(validator.step3.email)"
                    ></gwb-float-field>
                    <span
                        v-if="validator.step3.email.$error"
                        v-text="validator.step3.email.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>
                </template>

                <template v-if="formData.step2.regRole === 'worker'">
                    <gwb-float-field
                        v-model="formData.step3.country"
                        type="select"
                        id="country"
                        name="country"
                        :label="$lang('landing.signup_form3_worker_country')"
                        ref="country"
                        :error="validator.step3.country.$error"
                        @change="validator.step3.country.$touch && onCountryChange($event)"
                        @blur="touch(validator.step3.country)"
                    >
                        <template v-slot:options>
                            <option
                                v-for="(countryName, countryId) in availableCountries"
                                :value="countryId"
                            >
                                {{ countryName }}
                            </option>
                        </template>
                    </gwb-float-field>
                    <span
                        v-if="validator.step3.country.$error"
                        v-text="validator.step3.country.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>
                    <gwb-sign-up-city-field
                        v-model="formData.step3.address"
                        @details="Object.assign(formData.step3, $event)"
                        id="address"
                        name="address"
                        :country="formData.step3.country"
                        :label="$lang('landing.signup_form3_city')"
                        :error="validator.step3.address.$error"
                        @change="touch(validator.step3.address)"
                        @blur="touch(validator.step3.address)"
                    ></gwb-sign-up-city-field>
                    <span
                        v-if="validator.step3.address.$error"
                        v-text="validator.step3.address.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>

                    <gwb-float-field
                        v-if="showPhoneForWorker"
                        class="flex-1"
                        v-model="formData.step3.phone"
                        type="tel"
                        id="phone"
                        name="phone"
                        :initAreaCode="initPhoneAreaCode"
                        :label="`${$lang('m.phone')}`"
                        :error="validator.step3.phone.$error"
                        @change="touch(validator.step3.phone)"
                        @blur="touch(validator.step3.phone)"
                    ></gwb-float-field>

                    <span
                        v-if="validator.step3.phone.$error"
                        v-text="validator.step3.phone.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>

                </template>

                <template
                    v-if="
                        formData.step2.regRole === 'company' &&
                        !formData.step1.invite_colleague
                    "
                >
                    <gwb-float-field
                        v-model="formData.step3.company_name"
                        type="text"
                        id="company_name"
                        name="company_name"
                        :label="$lang('landing.signup_form3_company')"
                        :error="validator.step3.company_name.$error"
                        @change="touch(validator.step3.company_name)"
                        @blur="touch(validator.step3.company_name)"
                    ></gwb-float-field>
                    <span
                        v-if="validator.step3.company_name.$error"
                        v-text="validator.step3.company_name.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>
                    <gwb-float-field
                        type="select"
                        v-model="formData.step3.country"
                        id="country"
                        name="country"
                        :label="$lang('landing.signup_form3_company_country')"
                        :error="validator.step3.country.$error"
                        @change="
                            validator.step3.country.$touch &&
                                fillAreaCode(formData.step3.country)
                        "
                        @blur="touch(validator.step3.country)"
                    >
                        <template v-slot:options>
                            <option
                                v-for="(countryName, countryId) in availableCountries"
                                :value="countryId"
                            >
                                {{ countryName }}
                            </option>
                        </template>
                    </gwb-float-field>
                    <span
                        v-if="validator.step3.country.$error"
                        v-text="validator.step3.country.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>
                </template>

                <template v-if="formData.step2.regRole === 'company'">
                    <gwb-float-field
                        v-model="formData.step3.job_title"
                        type="text"
                        id="job_title"
                        name="job_title"
                        :label="$lang('landing.signup_form3_profession')"
                        :error="validator.step3.job_title.$error"
                        @change="touch(validator.step3.job_title)"
                        @blur="touch(validator.step3.job_title)"
                    ></gwb-float-field>
                    <span
                        v-if="validator.step3.job_title.$error"
                        v-text="validator.step3.job_title.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>

                    <gwb-float-field
                        class="flex-1"
                        v-model="formData.step3.phone"
                        type="tel"
                        id="phone"
                        name="phone"
                        :initAreaCode="initPhoneAreaCode"
                        :label="`${$lang('m.phone')}`"
                        :error="validator.step3.phone.$error"
                        @change="touch(validator.step3.phone)"
                        @blur="touch(validator.step3.phone)"
                    ></gwb-float-field>

                    <span
                        v-if="validator.step3.phone.$error"
                        v-text="validator.step3.phone.$errors[0].$message"
                        class="is-visible form-error"
                    ></span>
                </template>

                <div class="row align-center mt-2">
                    <div class="columns medium-12">
                        <button
                            id="signup-form-continue-to-step4-btn"
                            :class="{ invalid: validator.step3.$error, loading: loading }"
                            :disabled="loading"
                            class="
                                large
                                expanded
                                login
                                button
                                text-uppercase
                                bold
                            "
                        >
                            {{ $lang("landing.signup_form3_button") }}
                        </button>
                    </div>
                </div>

                <div class="mt-1 text-center">
                    <small>
                        {{ $lang("landing.signup_form2_terms1") }}
                        <a
                            id="signup-form-terms-link"
                            :href="termsLink"
                            target="_blank"
                            >{{ $lang("landing.signup_form2_terms2") }}</a
                        >
                    </small>
                </div>

                <div class="mt-1 text-center" v-if="!showCampaignCode">
                    <a
                        id="signup-form-show-campaign-code-link"
                        @click="showCampaignCode = !showCampaignCode"
                        >{{
                            $lang("landing.signup_form3_add_campaign_code")
                        }}</a
                    >
                </div>

                <gwb-float-field
                    v-if="showCampaignCode"
                    v-model="formData.step3.campaign_code"
                    type="text"
                    id="campaign_code"
                    name="campaign_code"
                    :label="$lang('landing.signup_form3_campaign_code')"
                ></gwb-float-field>

                <div class="mt-1 text-center semibold">
                    <a class="text-center" href="#" @click="gotoPreviousStep"
                        >&#10094; {{ $lang("m.back") }}</a
                    >
                </div>
            </div>

            <div
                class="signup-form__step"
                v-bind:class="[formData.step2.regRole]"
                v-show="activeStep === 4"
            >
                <p class="tc-purple text-center lead bold">
                    {{ $lang("landing.signup_form4_headline") }}
                </p>

                <div class="mt-1 text-center">
                    <p v-if="formData.step2.regRole === 'worker'">
                        <strong>
                            {{ $lang("landing.signup_form4_text1_worker") }}
                        </strong>
                    </p>
                    <p v-else-if="formData.step2.regRole === 'company'">
                        <strong>
                            {{ $lang("landing.signup_form4_text1_company") }}
                        </strong>
                    </p>

                    <p v-if="formData.step2.regRole === 'worker'">
                        {{ $lang("landing.signup_form4_text2_worker") }}
                    </p>
                    <p v-else-if="formData.step2.regRole === 'company'">
                        {{ $lang("landing.signup_form4_text2_company") }}
                    </p>

                    <p>{{ $lang("landing.signup_form4_text3") }}</p>

                    <a
                        id="signup-form-resend-verify-email-link"
                        class="my-3 block"
                        href="/sendVerifyEmail"
                        >{{ $lang("landing.signup_form4_button") }}</a
                    >
                </div>
            </div>
        </template>
    </gwb-form-v2>
</template>
<script>
import GwbFloatField from "./gwb-float-field.vue";
import GwbSignUpCityField from "./gwb-sign-up-city-field.vue";
import GwbSignUpCountryField from "./gwb-sign-up-country-field";
import GwbSelect from "gwbui/src/components/select";
import {
    email,
    helpers,
    maxLength,
    minLength,
    required,
    requiredIf,
    apiError,
} from "gwbui/src/components/form-v2/validators";
import mailcheck from "mailcheck";
import {
    track as mixpanelTrack,
    identify as mixpanelIdentify,
    setUserProperties as mixpanelSetUserProperties,
    MixpanelEvent,
} from "../mixpanel";

export default {
    name: "SignUpForm",

    components: {
        GwbFloatField,
        GwbSignUpCityField,
        GwbSignUpCountryField,
    },

    props: {
        action: {
            type: String,
            required: true,
        },

        invitationEmail: {
            type: String,
        },

        oAuthUser: {
            type: Object,
            default: () => null,
        },

        initPhoneAreaCode: {
            type: String,
            required: true,
        },

        countryOptions: {
            type: String,
            required: true,
        },

        signupWorkerPhoneEnabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            formData: {
                step1: {
                    email: this.invitationEmail || "",
                    password: "",
                    invite_colleague: null,
                },
                step2: {
                    regRole: "",
                },
                step3: {
                    first_name: "",
                    last_name: "",
                    email: "", // Needed when oAuth user doesn't have email
                    address: "",
                    country: window.gwbSrv.currentCountry,
                    latitude: "",
                    longitude: "",
                    gmdata: {},

                    company_name: "",
                    job_title: "",
                    phone: this.areaCodeFromId(window.gwbSrv.currentCountry),
                    campaign_code: "",
                },
            },

            activeStep: 1,
            initRegRole: "",
            passwordVisible: false,
            showCampaignCode: false,
            apiError: "",
            emailSuggestion: null,
            showPhoneForWorker: false
        };
    },

    created() {
        mailcheck.defaultDomains.push("hot.ee", "online.ee", "mail.ee");
        mailcheck.defaultTopLevelDomains.push("ee");
    },

    mounted() {
        this.prefillForm();
        if (this.oAuthUser) this.oAuthProgress(this.oAuthUser);
        gtmGaEvent("Signup", "Signup form was loaded", null, 0);

        this.togglePhoneVisibility()
    },

    computed: {
        availableCountries() {
            return JSON.parse(this.countryOptions);
        },
        // Messages live in the rules (SUP-346) — there is no separate validationMessages
        // computed under form-v2. Two messages ported from the old validationMessages tree
        // are intentionally dropped as dead: login-form's password.maxLength has no
        // equivalent here, and step3.address's `country` message has no matching rule
        // (the rule itself was already commented out below). The step3.country custom
        // rule's message is kept as the literal legacy fallback string, verbatim —
        // that rule never had a message key, so 0.7 fell back to showing the path itself.
        validationRules() {
            return {
                step1: {
                    email: {
                        required: helpers.withMessage(this.$lang("landing.form_error_4"), requiredIf(() => !this.oAuthUser)),
                        email: helpers.withMessage(this.$lang("landing.form_error_2"), email),
                        apiError: apiError("email"),
                    },
                    password: {
                        required: helpers.withMessage(this.$lang("landing.form_error_3r"), requiredIf(() => !this.oAuthUser)),
                        minLength: helpers.withMessage(this.$lang("landing.form_error_3r"), minLength(8)),
                        maxLength: helpers.withMessage(this.$lang("landing.form_error_3r"), maxLength(30)),
                        apiError: apiError("password"),
                    },
                },
                step2: {
                    regRole: {
                        required: helpers.withMessage(this.$lang("landing.form_error_8"), required),
                        apiError: apiError("regRole"),
                    },
                },
                step3: {
                    first_name: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), required),
                        not_email: helpers.withMessage(
                            this.$lang("validation.not_email", { attribute: this.$lang("landing.signup_form3_first") }),
                            (value) => !helpers.req(value) || !value.includes("@"),
                        ),
                        alphabet: helpers.withMessage(
                            this.$lang("validation.alphabetic-letters"),
                            (value) => !/[^a-zõäöüāčēģīķļņōŗšūžąęėį -]/iu.test(value),
                        ),
                        apiError: apiError("first_name"),
                    },
                    last_name: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), required),
                        alphabet: helpers.withMessage(
                            this.$lang("validation.alphabetic-letters"),
                            (value) => !/[^a-zõäöüāčēģīķļņōŗšūžąęėį -]/iu.test(value),
                        ),
                        not_email: helpers.withMessage(
                            this.$lang("validation.not_email", { attribute: this.$lang("landing.signup_form3_last") }),
                            (value) => !helpers.req(value) || !value.includes("@"),
                        ),
                        apiError: apiError("last_name"),
                    },
                    email: {
                        required: helpers.withMessage(this.$lang("landing.form_error_4"), requiredIf(() => {
                            // Required for OAuth users when OAuth didn't provide email
                            return this.oAuthUser && !this.oAuthUser.email;
                        })),
                        email: helpers.withMessage(this.$lang("landing.form_error_2"), email),
                        apiError: apiError("email"),
                    },
                    address: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), requiredIf(() => {
                            return this.formData.step2.regRole === "worker";
                        })),
                        //country: value => !helpers.req(value) || !!this.formData.step3.country,
                        apiError: apiError("address"),
                    },
                    country: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), requiredIf(() => {
                            return (
                                this.formData.step2.regRole === "worker" ||
                                (this.formData.step2.regRole === "company" &&
                                    !this.formData.step1.invite_colleague)
                            );
                        })),
                        country: helpers.withMessage(
                            "data.step3.country.country",
                            (value) => !helpers.req(value) || !!this.formData.step3.country,
                        ),
                        apiError: apiError("country"),
                    },
                    company_name: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), requiredIf(() => {
                            return (
                                this.formData.step2.regRole === "company" &&
                                !this.formData.step1.invite_colleague
                            );
                        })),
                        apiError: apiError("company_name"),
                    },
                    job_title: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), requiredIf(() => {
                            return this.formData.step2.regRole === "company";
                        })),
                        apiError: apiError("job_title"),
                    },
                    phone: {
                        required: helpers.withMessage(this.$lang("validation.anon_required"), requiredIf(() => {
                            return this.formData.step2.regRole === "company";
                        })),
                        format: helpers.withMessage(
                            this.$lang("landing.form_error_9"),
                            (value) => !helpers.req(value) || /^\+[0-9 ]{7,20}$/.test(value),
                        ),
                        apiError: apiError("phone"),
                    },
                },
            };
        },

        termsLink() {
            if (this.formData.step2.regRole === "worker")
                return "/terms/new-worker";
            else if (this.formData.step2.regRole === "company")
                return "/terms/new-company";
            return "/terms";
        },
    },

    watch: {
        activeStep(val, oldVal) {
            if (val === 2)
                gtmGaEvent(
                    "Signup",
                    "Signup form rolepick step was loaded",
                    null,
                    1
                );
            else if (val === 3) {
                if (this.formData.step2.regRole === "worker") {
                    gtmGaEvent(
                        "Signup",
                        "Signup form worker info step was loaded",
                        null,
                        2
                    );
                } else if (this.formData.step2.regRole === "company") {
                    gtmGaEvent(
                        "Signup",
                        "Signup form company info step was loaded",
                        null,
                        2
                    );
                }
            }
        },
        initRegRole(val) {
            this.formData.step2.regRole = val;
        },
        "formData.step2.regRole": function () {
            this.formData.step3.address = "";
            this.formData.step3.phone = "";
            this.formData.step3.company_name = "";
            this.formData.step3.job_title = "";
        },
        "formData.step3.country": function () {
            const country = typeof this.formData.step3.country === "string"
                ? parseInt(this.formData.step3.country)
                : this.formData.step3.country;
            this.phoneAreaCode =
                window.gwbSrv.phoneHelpers[country];
        },
        // Sync step3.email to step1.email for OAuth users (so merge doesn't overwrite)
        "formData.step3.email": function (val) {
            if (this.oAuthUser) {
                this.formData.step1.email = val;
            }
        },
    },

    methods: {
        checkEmailSuggestion(email) {
            mailcheck.run({
                email,
                suggested: (suggestion) => {
                    this.emailSuggestion = suggestion;
                },
                empty: () => {
                    this.emailSuggestion = null;
                },
            });
        },

        gotoStep2(validator) {
            // SUP-346: 0.7's group $touch dropped each field's server error; v2's does not.
            this.$refs.form.clearApiErrorsForResubmit(validator.step1);
            validator.step1.$touch();

            if (!validator.step1.$invalid) {
                // GWB-402: signup:started fires only once validation passes (email path).
                this.trackSignupStarted("email");

                if (this.initRegRole) {
                    this.activeStep = 3;
                } else {
                    this.activeStep = 2;
                }
            }
        },

        // GWB-402: signup:started is anonymous (no account yet). signup_method is sent ONLY as an
        // event property here — NOT via people.set. A people.set on an anonymous visitor creates an
        // orphan profile keyed to the device id that identify() never folds into the user; the
        // signup_method people property is set instead on signup:completed, after identify() (the
        // server supplies it in the completion payload). The event itself merges into the user when
        // signup:completed calls identify().
        //
        // useBeacon: the OAuth anchors navigate away immediately after this fires, which can cancel a
        // normal XHR mid-flight and undercount signup:started — sendBeacon survives the navigation.
        // The email path stays on-page, so it uses the default XHR transport.
        trackSignupStarted(signupMethod, { useBeacon = false } = {}) {
            mixpanelTrack(
                MixpanelEvent.SIGNUP_STARTED,
                { signup_method: signupMethod },
                useBeacon ? { transport: "sendBeacon" } : undefined
            );
        },

        // GWB-402: identify() merges the anonymous signup:started session into the new user
        // profile, then we send the server-derived signup properties (incl. signup_method, which
        // the feature stamps onto the payload — email vs OAuth provider — and company_name from the
        // form for company signups) as BOTH event properties on signup:completed (so the event can
        // be segmented by signup_method/user_type/user_country/etc. directly) and people properties
        // on the now-identified profile. signup_method is set here, not on the anonymous
        // signup:started event, to avoid orphan anonymous profiles.
        trackSignupCompleted(mixpanelPayload) {
            if (!mixpanelPayload) {
                return;
            }

            mixpanelIdentify(mixpanelPayload.user_id);

            const userProperties = { ...(mixpanelPayload.user_properties || {}) };
            if (
                this.formData.step2.regRole === "company" &&
                this.formData.step3.company_name
            ) {
                userProperties.company_name = this.formData.step3.company_name;
            }
            mixpanelSetUserProperties(userProperties);

            mixpanelTrack(MixpanelEvent.SIGNUP_COMPLETED, userProperties);
        },

        gotoStep3(validator) {
            // SUP-346: 0.7's group $touch dropped each field's server error; v2's does not.
            this.$refs.form.clearApiErrorsForResubmit(validator.step2);
            validator.step2.$touch();

            if (!validator.step2.$invalid) {
                this.activeStep = 3;
            }
        },

        gotoPreviousStep() {
            if (
                this.activeStep === 3 &&
                this.initRegRole &&
                this.formData.step2.regRole
            )
                this.activeStep = 1;
            else if (this.activeStep > 1) this.activeStep -= 1;
        },

        signupUser() {
            const data = Object.keys(this.formData).reduce((acc, key) => {
                acc = Object.assign({}, this.formData[key], acc);
                return acc;
            }, {});

            return this.$http
                .post(this.action, data)
                .then((response) => {
                    if (response.data.status === "success") {
                        this.trackSignupCompleted(response.data.mixpanel);

                        // GWB-589/590 §3.1. An ordered LIST built server-side for BOTH audiences:
                        // this one endpoint serves worker and company signups (regRole is a form
                        // field), so the action dispatches b2c_signup_success or b2b_signup_success
                        // from the subject and the list is empty only for a superadmin. GWB-589
                        // shipped this block with only the b2c half live — a company signup pushed
                        // nothing because the server returned null — and GWB-590 turned the b2b half
                        // on without touching this file. The list itself IS the gate, so there is
                        // still nothing audience-specific to keep in sync here. The order is the
                        // server's: this page rendered for an anonymous visitor, so the list leads
                        // with the identity push that the load-time one could not carry.
                        //
                        // Safe immediately before the oAuth branch's location.assign below: gtm.js
                        // processes a dataLayer push synchronously, which is why gtmGaPageview has
                        // sat four lines above that same redirect for years.
                        if (response.data.ga4) {
                            window.dataLayer = window.dataLayer || [];
                            response.data.ga4.forEach((event) => window.dataLayer.push(event));
                        }

                        if (this.formData.step2.regRole === "worker") {
                            gtmGaPageview("/frame/auth/workerDone");
                        } else if (this.formData.step2.regRole === "company") {
                            gtmGaPageview("/frame/auth/companyDone");
                        }

                        // oAuth user details confirmation redirects to user profile
                        if (this.oAuthUser && response.data.newPage) {
                            window.location.assign(response.data.newPage);
                        } else {
                            this.activeStep = 4; // otherwise on signup show 4 stage of form with welcome msg.
                        }
                    } else if (response.data.status === "error") {
                        this.apiError = response.data.message;
                    }

                    return response;
                })
                .catch((error) => {
                    // error.response is undefined on a transport-level failure (network
                    // error / canceled request / CORS). Guard before dereferencing so the
                    // catch itself doesn't throw a secondary TypeError (same class as
                    // GWB-479 in Form.vue), and fall back to a generic message when there's
                    // no server response.
                    this.apiError = error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : this.$lang("outerweb.guest-apply:errors:generic");
                    throw error;
                });
        },

        prefillForm() {
            const regRole = this.$getURLParameter("regRole");
            const inviteColleague = this.$getURLParameter("inviteColleague");
            if (inviteColleague) {
                this.formData.step1.invite_colleague = inviteColleague;
                this.initRegRole = "company";
            } else if (["worker"].indexOf(regRole) !== -1) {
                this.initRegRole = regRole;
            }
        },

        oAuthProgress(user) {
            // Only set email in form if OAuth didn't provide it (field will be shown)
            // If OAuth provided email, user already has it - don't put in form to avoid validation
            if (!user.email) {
                this.formData.step3.email = "";
            }
            this.formData.step3.first_name = user.first_name || "";
            this.formData.step3.last_name = user.last_name || "";
            this.activeStep = 2;
        },

        areaCodeFromId(id) {
            return id ? gwbSrv.phoneHelpers[id] : this.initPhoneAreaCode;
        },

        fillAreaCode(countryId) {
            let newPhone = countryId ? this.areaCodeFromId(countryId) : this.initPhoneAreaCode;
            if (!this.formData.step3.phone || (this.formData.step3.phone.match(/\+([0-9]{3}\b)/))) {
                this.formData.step3.phone = newPhone;
                this.initPhoneAreaCode = newPhone;
            }
        },

        onCountryChange(event) {
            this.formData.step3.address = '';

            this.togglePhoneVisibility();
        },

        togglePhoneVisibility() {

            if (!this.signupWorkerPhoneEnabled) {
                return false;
            }

            if (this.areaCodeFromId(this.formData.step3.country) === '+372') {
                this.showPhoneForWorker = false
                this.formData.step3.phone = '';

                return false;
            }

            this.showPhoneForWorker = true
            this.fillAreaCode(this.formData.step3.country);

            return true;
        }
    },
};
</script>
